import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import chatStore from '../stores/chatStore';
import { useTheme } from '../theme/ThemeContext';
import { formatUsdCost, getUsdRubRate } from '../utils/money';

function balanceTone(rub) {
    if (rub < 150) return 'red';
    if (rub < 300) return 'yellow';
    return 'green';
}

const toneClasses = {
    green: {
        light: 'border-emerald-300/80 bg-emerald-50 text-emerald-800 shadow-emerald-100',
        dark: 'border-emerald-500/40 bg-emerald-500/15 text-emerald-200',
        dot: 'bg-emerald-500',
        btn: 'hover:bg-emerald-500/20',
    },
    yellow: {
        light: 'border-amber-300/80 bg-amber-50 text-amber-900 shadow-amber-100',
        dark: 'border-amber-500/40 bg-amber-500/15 text-amber-200',
        dot: 'bg-amber-400',
        btn: 'hover:bg-amber-500/20',
    },
    red: {
        light: 'border-red-300/80 bg-red-50 text-red-800 shadow-red-100',
        dark: 'border-red-500/40 bg-red-500/15 text-red-200',
        dot: 'bg-red-500',
        btn: 'hover:bg-red-500/20',
    },
};

const CreditsBalance = observer(({ compact = false }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    useEffect(() => {
        chatStore.loadCredits({ force: true });
        const id = setInterval(() => chatStore.loadCredits({ force: true }), 60_000);
        return () => clearInterval(id);
    }, [chatStore.provider]);

    const remainingUsd = chatStore.credits?.remaining;
    const loading = chatStore.creditsLoading;
    const error = chatStore.creditsError;

    const refresh = () => chatStore.loadCredits({ force: true });

    if (compact) {
        if (remainingUsd == null) return null;
        const rate = getUsdRubRate(chatStore.settings);
        const rub = remainingUsd * rate;
        const tone = balanceTone(rub);
        const colors = toneClasses[tone];
        return (
            <button
                type="button"
                title={`Баланс: ${formatUsdCost(remainingUsd, chatStore.settings)}`}
                onClick={refresh}
                className={`mx-auto flex h-8 w-8 items-center justify-center rounded-full border ${
                    isDark ? colors.dark : colors.light
                }`}
            >
                <span className={`h-2.5 w-2.5 rounded-full ${colors.dot}`} />
            </button>
        );
    }

    let label = 'Баланс…';
    let tone = 'green';
    if (error && remainingUsd == null) {
        label = 'Недоступен';
        tone = 'red';
    } else if (loading && remainingUsd == null) {
        label = 'Загрузка…';
    } else if (remainingUsd != null) {
        const rate = getUsdRubRate(chatStore.settings);
        const rub = remainingUsd * rate;
        tone = balanceTone(rub);
        label = formatUsdCost(remainingUsd, chatStore.settings) || '—';
    }

    const colors = toneClasses[tone];

    return (
        <div
            className={`mb-2 w-full rounded-xl border px-3.5 py-3 shadow-sm ${
                isDark ? colors.dark : colors.light
            }`}
        >
            <div className="flex items-center gap-3">
                <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${colors.dot}`} />
                <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold uppercase tracking-wide opacity-75">
                        Баланс
                    </div>
                    <div className="truncate text-xl font-bold leading-tight tabular-nums">
                        {label}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={refresh}
                    disabled={loading}
                    title="Обновить баланс"
                    aria-label="Обновить баланс"
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition disabled:opacity-50 ${colors.btn} ${
                        isDark ? 'hover:bg-white/10' : 'hover:bg-black/5'
                    }`}
                >
                    <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden
                        className={loading ? 'animate-spin' : ''}
                    >
                        <path
                            d="M4 4v6h6M20 20v-6h-6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M20 9A8 8 0 006.5 6.5L4 10M4 15a8 8 0 0013.5 2.5L20 14"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
            </div>
        </div>
    );
});

export default CreditsBalance;
